<template>
  <div class="modal-card" style="width: 700px">
    <header class="modal-card-head">
      <p class="modal-card-title">
        <span class="icon-text">
          <span class="icon has-text-warning">
            <i class="fas fa-star"></i>
          </span>
          <span>Calificar Actividad</span>
        </span>
      </p>
      <button type="button" class="delete" aria-label="close" @click="$emit('close')"></button>
    </header>

    <section class="modal-card-body">
      <!-- Información de la Actividad -->
      <div class="box has-background-light mb-4">
        <h6 class="subtitle is-6 has-text-weight-bold">
          {{ activity.academic_activity ? activity.academic_activity.name : 'Actividad' }}
        </h6>
        <p v-if="activity.description"><strong>Descripción:</strong> {{ activity.description }}</p>
        <p><strong>Horas Totales:</strong> {{ totalHours }}</p>
      </div>

      <!-- Evidencias -->
      <div class="mb-4">
        <h6 class="subtitle is-6">
          <span class="icon-text">
            <span class="icon has-text-info">
              <i class="fas fa-paperclip"></i>
            </span>
            <span>Evidencias Adjuntas ({{ activity.evidences ? activity.evidences.length : 0 }})</span>
          </span>
        </h6>
        <div class="content" v-if="activity.evidences && activity.evidences.length > 0">
          <ul>
            <li 
              v-for="evidence in activity.evidences" 
              :key="evidence.id"
            >
              <a :href="getEvidenceUrl(evidence)" target="_blank">
                <span class="icon is-small">
                  <i class="fas fa-file"></i>
                </span>
                <span>{{ evidence.file_name || 'Evidencia' }}</span>
              </a>
            </li>
          </ul>
        </div>
        <p v-else class="has-text-grey-light">Sin evidencias adjuntas</p>
      </div>

      <!-- Formulario de Calificación -->
      <b-field label="Nota (0.00 - 4.00):" :type="scoreError ? 'is-danger' : ''" :message="scoreError">
        <b-input 
          type="number" 
          v-model="score"
          min="0"
          max="4"
          step="0.01"
          @input="validateScore"
          placeholder="Ej: 3.50"
        />
      </b-field>

      <!-- Etiqueta de Calificación -->
      <b-message 
        v-if="score !== null && score !== '' && !scoreError" 
        :type="getScoreLabelType()"
        class="mb-4"
      >
        <strong>Categoría:</strong> 
        <span class="has-text-weight-bold">{{ scoreCategory }}</span>
      </b-message>

      <!-- Observaciones -->
      <b-field label="Observaciones (opcional):">
        <b-input 
          type="textarea"
          v-model="observations"
          rows="4"
          placeholder="Ingrese observaciones sobre la calificación..."
        />
      </b-field>
    </section>

    <footer class="modal-card-foot">
      <b-button @click="$emit('close')" type="is-light">
        Cancelar
      </b-button>
      <b-button 
        @click="saveScore" 
        type="is-primary"
        icon-left="save"
        :disabled="!canSave"
        :loading="saving"
      >
        Guardar Calificación
      </b-button>
    </footer>
  </div>
</template>

<script>
export default {
  name: 'ActivityScoreModal',
  
  props: {
    activity: {
      type: Object,
      required: true
    },
    performanceId: {
      type: Number,
      required: true
    }
  },
  
  data() {
    return {
      score: this.activity.precalification_score || '',
      observations: this.activity.precalification_comment || '',
      scoreError: null,
      saving: false
    }
  },
  
  computed: {
    totalHours() {
      const h1 = parseFloat(this.activity.hours_completed_semester_1) || 0
      const h2 = parseFloat(this.activity.hours_completed_semester_2) || 0
      return h1 + h2
    },
    
    scoreCategory() {
      if (this.score === null || this.score === '') return ''
      const s = parseFloat(this.score)
      if (s >= 3.70) return 'Sobresaliente'
      if (s >= 2.80) return 'Bueno'
      if (s >= 2.00) return 'Aceptable'
      if (s >= 1.50) return 'Condicional'
      return 'Insuficiente'
    },
    
    canSave() {
      return this.score !== null && 
             this.score !== '' &&
             !this.scoreError
    }
  },
  
  methods: {
    validateScore() {
      const s = parseFloat(this.score)
      
      if (this.score === '') {
        this.scoreError = null
        return
      }
      
      if (isNaN(s)) {
        this.scoreError = 'Ingrese un número válido'
        return
      }
      
      if (s < 0 || s > 4) {
        this.scoreError = 'La nota debe estar entre 0.00 y 4.00'
        return
      }
      
      // Validar 2 decimales
      const decimalPart = this.score.toString().split('.')[1]
      if (decimalPart && decimalPart.length > 2) {
        this.scoreError = 'Use máximo 2 decimales'
        return
      }
      
      this.scoreError = null
    },
    
    async saveScore() {
      if (!this.canSave) return
      
      this.saving = true
      
      try {
        // Formatear el score a máximo 2 decimales
        const formattedScore = parseFloat(this.score).toFixed(2)
        
        const response = await fetch(
          `/performance_precalifications/${this.performanceId}/score_activity`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-Token': this.getCsrfToken()
            },
            body: JSON.stringify({
              activity_id: this.activity.id,
              score: formattedScore,
              observations: this.observations
            })
          }
        )
        
        const data = await response.json()
        
        if (data.success) {
          this.$buefy.toast.open({
            message: 'Actividad calificada exitosamente',
            type: 'is-success',
            duration: 3000
          })
          // Emitir el evento con datos de la respuesta para actualización eficiente
          this.$emit('scored', {
            activity_id: this.activity.id,
            final_score: data.final_score,
            weighted_score_breakdown: data.weighted_score_breakdown
          })
        } else {
          this.$buefy.toast.open({
            message: 'Error: ' + data.error,
            type: 'is-danger',
            duration: 4000
          })
        }
      } catch (error) {
        console.error('Error guardando calificación:', error)
        this.$buefy.toast.open({
          message: 'Error al guardar la calificación',
          type: 'is-danger',
          duration: 4000
        })
      } finally {
        this.saving = false
      }
    },
    
    getEvidenceUrl(evidence) {
      return `/evidences/${evidence.id}`
    },
    
    getScoreLabelType() {
      const s = parseFloat(this.score)
      if (s >= 3.70) return 'is-success'
      if (s >= 2.80) return 'is-info'
      if (s >= 2.00) return 'is-warning'
      return 'is-danger'
    },
    
    getCsrfToken() {
      const meta = document.querySelector('meta[name="csrf-token"]')
      return meta ? meta.content : ''
    }
  }
}
</script>

<style scoped>
.modal-card-foot {
  justify-content: flex-end;
}
</style>
